import { useDispatch, useSelector } from "react-redux";
import {
  getClientListAction,
  getClientByIdAction,
  createClientAction,
  updateClientAction,
  deleteClientAction,
  getCurrentClientAction,
  switchClientAction,
  getClientStatsAction,
  getClientPermissionsAction,
  updateClientPermissionsAction,
  clearCurrentClientAction,
  // New actions
  searchClientsAction,
  getClientStatisticsAction,
  updateClientStatusAction,
  updateClientPreferencesAction,
  getClientPreferencesAction,
  updateClientOpeningsAction,
  getClientOpeningsAction,
  getClientUsersAction,
  addClientUserAction,
  createClientAdminAction,
  createClientEmployeeAction,
  updateClientUserAction,
  removeClientUserAction,
  getClientAdminsAction,
  getClientEmployeesAction,
  getClientDashboardAction,
  getClientPipelineAction,
  getClientEvaluationAnalyticsAction
} from "../actions/clientActions";
import {
  setError as SetError,
  setClientError as SetClientError,
  clearClientState,
  selectClientLoading,
  selectClientError,
  selectClientList,
  selectCurrentClient,
  selectClientLoadingState,
  selectClientErrorState,
  selectClientStats,
  selectClientPermissions,
  selectClientFilters,
  selectClientPagination,
  selectAvailableClients
} from "../slices/clientSlice";

const useClient = () => {
  const dispatch = useDispatch();

  // Selectors
  const clientList = useSelector(selectClientList);
  const currentClient = useSelector(selectCurrentClient);
  const loading = useSelector(selectClientLoading);
  const error = useSelector(selectClientError);
  const clientLoading = useSelector(selectClientLoadingState);
  const clientError = useSelector(selectClientErrorState);
  const clientStats = useSelector(selectClientStats);
  const clientPermissions = useSelector(selectClientPermissions);
  const filters = useSelector(selectClientFilters);
  const availableClients = useSelector(selectAvailableClients);
  const pagination = useSelector(selectClientPagination);

  // Error handling
  const setError = (error) => {
    return dispatch(SetError(error));
  };

  const setClientError = (error) => {
    return dispatch(SetClientError(error));
  };

  // Core client operations
  const loadClientList = async (params = {}) => {
    return dispatch(getClientListAction(params));
  };

  const loadClientById = async (clientId) => {
    return dispatch(getClientByIdAction(clientId));
  };

  const createNewClient = async (clientData) => {
    return dispatch(createClientAction(clientData));
  };

  const updateExistingClient = async (clientId, clientData) => {
    return dispatch(updateClientAction(clientId, clientData));
  };

  const deleteExistingClient = async (clientId) => {
    return dispatch(deleteClientAction(clientId));
  };

  // Current client operations
  const loadCurrentClient = async () => {
    return dispatch(getCurrentClientAction());
  };

  const switchToClient = async (clientId) => {
    return dispatch(switchClientAction(clientId, availableClients));
  };

  const clearCurrentClient = async () => {
    return dispatch(clearCurrentClientAction());
  };

  // Client statistics and permissions
  const loadClientStats = async (clientId) => {
    return dispatch(getClientStatsAction(clientId));
  };

  const loadClientPermissions = async (clientId) => {
    return dispatch(getClientPermissionsAction(clientId));
  };

  const updateClientPermissions = async (clientId, permissions) => {
    return dispatch(updateClientPermissionsAction(clientId, permissions));
  };

  // Clear state
  const clearClientStateAction = () => {
    return dispatch(clearClientState());
  };

  // NEW FUNCTIONS BASED ON CLIENT MANAGEMENT DOCUMENTATION

  // Search clients
  const searchClients = async (query) => {
    return dispatch(searchClientsAction(query));
  };

  // Get client statistics (admin dashboard)
  const getClientStatistics = async () => {
    return dispatch(getClientStatisticsAction());
  };

  // Update client status
  const updateClientStatus = async (clientId, statusData) => {
    return dispatch(updateClientStatusAction(clientId, statusData));
  };

  // Update client preferences
  const updateClientPreferences = async (clientId, preferencesData) => {
    return dispatch(updateClientPreferencesAction(clientId, preferencesData));
  };

  // Get client preferences
  const getClientPreferences = async (clientId) => {
    return dispatch(getClientPreferencesAction(clientId));
  };

  // Update client openings
  const updateClientOpenings = async (clientId, openingsData) => {
    return dispatch(updateClientOpeningsAction(clientId, openingsData));
  };

  // Get client openings
  const getClientOpenings = async (clientId) => {
    return dispatch(getClientOpeningsAction(clientId));
  };

  // Get client users
  const getClientUsers = async (clientId) => {
    return dispatch(getClientUsersAction(clientId));
  };

  // Add client user
  const addClientUser = async (clientId, userData) => {
    return dispatch(addClientUserAction(clientId, userData));
  };

  // Create client admin
  const createClientAdmin = async (clientId, userData) => {
    return dispatch(createClientAdminAction(clientId, userData));
  };

  // Create client employee
  const createClientEmployee = async (clientId, userData) => {
    return dispatch(createClientEmployeeAction(clientId, userData));
  };

  // Update client user
  const updateClientUser = async (clientId, userId, userData) => {
    return dispatch(updateClientUserAction(clientId, userId, userData));
  };

  // Remove client user
  const removeClientUser = async (clientId, userId) => {
    return dispatch(removeClientUserAction(clientId, userId));
  };

  // Get client admins
  const getClientAdmins = async (clientId) => {
    return dispatch(getClientAdminsAction(clientId));
  };

  // Get client employees
  const getClientEmployees = async (clientId) => {
    return dispatch(getClientEmployeesAction(clientId));
  };

  // Get client dashboard
  const getClientDashboard = async (clientId) => {
    return dispatch(getClientDashboardAction(clientId));
  };

  // Get client pipeline
  const getClientPipeline = async (clientId) => {
    return dispatch(getClientPipelineAction(clientId));
  };

  // Get client evaluation analytics
  const getClientEvaluationAnalytics = async (clientId) => {
    return dispatch(getClientEvaluationAnalyticsAction(clientId));
  };

  return {
    // State
    clientList,
    currentClient,
    loading,
    error,
    clientLoading,
    clientError,
    clientStats,
    clientPermissions,
    filters,
    pagination,
    availableClients,
    // Core operations
    loadClientList,
    loadClientById,
    createNewClient,
    updateExistingClient,
    deleteExistingClient,
    
    // Current client operations
    loadCurrentClient,
    switchToClient,
    
    // Statistics and permissions
    loadClientStats,
    loadClientPermissions,
    updateClientPermissions,
    
    // Error handling
    setError,
    setClientError,
    clearClientState: clearClientStateAction,

    // NEW FUNCTIONS
    searchClients,
    getClientStatistics,
    updateClientStatus,
    updateClientPreferences,
    getClientPreferences,
    updateClientOpenings,
    getClientOpenings,
    getClientUsers,
    addClientUser,
    createClientAdmin,
    createClientEmployee,
    updateClientUser,
    removeClientUser,
    getClientAdmins,
    getClientEmployees,
    getClientDashboard,
    getClientPipeline,
    getClientEvaluationAnalytics
  };
};

export default useClient;
